<!--
******************************************************************************************************
Program ID:
Author:             Terence Ng
Date:               2018-03-25
Purpose:            A module for providing common detail table function
Inputs:             exportWithTimeInDateField:  flag for export time if the field is a date field
                    removeUrl:                  URL for update, the record will mark as marked delete
                    enableAddRecord:            enable add new record button or not
                    tableProvider:              table object
                    tableFields:                key value pair of database table column and display value
                    layerUrl:                   the URL of the required spatial data of feature class
                    perPage:                    number of record show per page
                    tableCaption:
                    tableId:                    element id of DOM object
                    alwaysEmitAllRecords:       return all record or not
                    enableFilterByMap:          enable filter by map button or not
                    isFilterByMap:              status of filter by map
                    disableEndFilter:           display end filter button or not
                    tableOpacity:               opacity of table
                    enableEmitAllRecordsButton: enable batch update button
                    queryProviderType:          determine which way to export CSV(through REST or API)
                    exportRequire:              number of record return each time
Returns:
Copyright:          All the intellectual rights belong to  Airport Authority Hong Kong
------------------------------------------------------------------------------------------------------
Revision History
------------------------------------------------------------------------------------------------------
Initial Version:    2018-03-25
******************************************************************************************************
-->
<!--Usage


 input: (function) tableProvider 
 e.g

    function myProvider (ctx) {
        let promise = axios.get('/some/url?page=' + ctx.currentPage + '&size=' + ctx.perPage)

        // Must return a promise that resolves to an array of items
        return promise.then((data) => {
            // Pluck the array of items off our axios response
            let items = data.items
            // Must return an array of items or an empty array if an error occurred
            return(items || [])
        })
    }

 //ref: https://bootstrap-vue.js.org/docs/components/table/#using-items-provider-functions

 input: (array)(optional) tableFields
 e.g 
 [{key:'CORE_ID',label:'CORE_ID'},{key:'ITSM_20',label:'ITSM_20'}],

 input: (string)(optional) layerUrl
 require to provide if needed to sort by shape area or length

-->
<template>
  
  <div v-bind:style="opacity">  
    <div class="mt-1 detail-table-icons">
        <b-button v-if='isFilterByMap && enableFilterByMap' variant="success" class='' v-on:click='toggleFilterMap' title="Filter By Map">
            <span class="esri-icon-applications" aria-label="Filter By Map"></span>
        </b-button>
        <b-button v-if='!(isFilterByMap) && enableFilterByMap' class='' v-on:click='toggleFilterMap' title="Filter By Map">
            <span class="esri-icon-applications" aria-label="Filter By Map"></span>
        </b-button> 
        <!-- <b-button v-if='enableEmitAllRecordsButton' v-on:click="queryAllRecords_bak"><span class="esri-icon-plus"></span></b-button> -->
        <b-button v-if='enableAddRecord' v-on:click="add"  title="Add Record"><span class="esri-icon-plus"></span></b-button>
        <!-- <b-button v-if='enableEmitAllRecordsButton' v-on:click="queryAllRecords_bak"><span class="esri-icon-plus"></span></b-button> -->
        <b-button v-if='enableEmitAllRecordsButton' v-on:click="queryAllRecords"  title="Batch Update"><span class="esri-icon-duplicate"></span></b-button>
        <b-button v-on:click="download"  title="Export CSV"><span class="esri-icon-table"/> <span class="esri-icon-right-arrow"/></b-button>
        <!-- <b-button class='' v-on:click='onFilterByMap' title="Filter By Map">
            <span class="esri-icon-applications" aria-label="Filter By Map"></span>
        </b-button>      -->
        <b-button v-on:click="endFilter" v-if='!disableEndFilter'  title="End Filter">End Filter</b-button>
    </div>
    <div class="detail-table-icons">
        <b-button v-on:click="firstPage" :disabled="isBusy" ref="firstPage" title="First Page"><span class="esri-icon-beginning"></span>
        </b-button>
        <b-button v-on:click="prevPage" :disabled="isBusy" ref="prevPage" title="Previous Page"><span class="esri-icon-reverse"></span>
        </b-button>
        <b-button v-on:click="nextPage" :disabled="isBusy" ref="nextPage" title="Next Page"><span class="esri-icon-forward" ></span>
        </b-button>
        <b-button v-on:click="lastPage" :disabled="isBusy" ref="lastPage" title="Last Page"><span class="esri-icon-end"></span>
        </b-button> 
        
        <span>Page:{{currentPage}}/{{getLastPage}}</span>
        <span><input style='width:4em;' :disabled="isBusy" placeholder='Page' v-model='goToPage'><b-button  title="Go to Page" :disabled="isBusy" v-on:click='jumpToPage'>Go</b-button></span>
        <br/>
        <span>Total Record:{{totalRows}}</span>
        <span v-if='orderByFields.length>0'>Sort By: {{orderByFieldsLabel.join(',')}} <b-button title="Cancel Ordering" v-on:click='resetOrderBy'>Cancel</b-button> </span>
        <span v-show='isBusy' class="spinner">⌛</span>
    </div>
  <b-table 
           :id="tableId"
           responsive small bordered hover striped
           show-empty
           v-on:head-clicked="onHeadClicked"
           :busy.sync="isBusy" ref="table" class="detail-table"
           :items="tableProvider" :fields="tableFields"
           :currentPage="currentPage"
           :perPage="perPage"
           :caption="tableCaption"
           >
      <template slot="info" slot-scope="row">
        <!-- We use @click.stop here to prevent a 'row-clicked' event from also happening -->
        <b-button @click.stop="row.toggleDetails" size='sm' class="">
          <span class='esri-icon-layer-list'></span>
        </b-button>
      </template>      
      <template slot="pan" slot-scope="row">
        <!-- We use @click.stop here to prevent a 'row-clicked' event from also happening -->
        <b-button title="pan" @click.stop="pan(row.item, row.index, $event.target)" size='sm' class="">
          <span class='esri-icon-pan'></span>
        </b-button>
      </template>
      <!-- slot for adding button in sensors details table  -->
      <template slot="log" slot-scope="row">
        <!-- We use @click.stop here to prevent a 'row-clicked' event from also happening -->
        <b-button title="log" @click.stop="log(row.item, row.index, $event.target)" size='sm' class="">
          <span class='esri-icon-table'></span>
        </b-button>
      </template>
      <template slot="edit" slot-scope="row">
        <!-- We use @click.stop here to prevent a 'row-clicked' event from also happening -->
        <b-button title="edit" @click.stop="edit(row.item, row.index, $event.target)"  size='sm' class="">
         <span class='esri-icon-edit'></span>
        </b-button>
      </template>
      <template slot="image" slot-scope="row">
        <!-- We use @click.stop here to prevent a 'row-clicked' event from also happening -->
        <b-button title="image" @click.stop="image(row.item, row.index, $event.target)"  size='sm' class="">
         <span class='esri-icon-media'></span>
        </b-button>
      </template>      
      <template slot="report" slot-scope="row">
        <!-- We use @click.stop here to prevent a 'row-clicked' event from also happening -->
        <b-button title="report" @click.stop="report(row.item, row.index, $event.target)"  size='sm' class="">
         <span class='esri-icon-documentation'></span>
        </b-button>
      </template>
      
      <template slot="viewResults" slot-scope="row">
        <!-- We use @click.stop here to prevent a 'row-clicked' event from also happening -->
        <b-button title="View" @click.stop="viewResults(row.item, row.index, $event.target)"  size='sm' class="">
         <span class='esri-icon-review'></span>
        </b-button>
      </template>
      <template slot="editResults" slot-scope="row">
        <!-- We use @click.stop here to prevent a 'row-clicked' event from also happening -->
        <b-button title="Load" @click.stop="editResults(row.item, row.index, $event.target)"  size='sm' class="">
         <span class='esri-icon-download'></span>
        </b-button>
      </template>
      <template slot="removeRelated" slot-scope="row">
        <!-- We use @click.stop here to prevent a 'row-clicked' event from also happening -->
        <b-button title="removeRelated" @click.stop="removeRelated(row.item, row.index, $event.target)"  size='sm' class="">
         <span class='esri-icon-trash'></span>
        </b-button>
      </template>
      <template slot="remove" slot-scope="row">
        <!-- We use @click.stop here to prevent a 'row-clicked' event from also happening -->
        <b-button title="remove" @click.stop="remove(row.item, row.index, $event.target)"  size='sm' class="">
         <span class='esri-icon-trash'></span>
        </b-button>
      </template>

      <!--audit logs per row globalid. to be done-->
      <template slot="audit" slot-scope="row">
        <!-- We use @click.stop here to prevent a 'row-clicked' event from also happening -->
        <b-button title="audit" @click.stop="audit(row.item, row.index, $event.target)" size='sm' class="">
          <span class='esri-icon-review'></span>
        </b-button>
      </template> 
      <template slot="PARAMETER" slot-scope="row">
        <b-btn :id="'parameter-popover-'+row.index" variant="primary">
          Click to View
        </b-btn>
        <b-popover :target="'parameter-popover-'+row.index"
                   title="PARAMETER"
                   triggers="click"
                   >
             <pre style='background-color:white;max-height:200px;overflow-y:scroll;'>{{JSON.stringify(JSON.parse(row.item['PARAMETER']),null,2)}}</pre>
        </b-popover>          
      </template>
      <template slot="MESSAGE" slot-scope="row">
        <b-btn :id="'message-popover-'+row.index" variant="primary">
          Click to View
        </b-btn>
        <b-popover :target="'message-popover-'+row.index"
                   title="MESSAGE"
                   triggers="click"
                   >
             <pre style='background-color:white;max-height:200px;overflow-y:scroll;'>{{JSON.stringify(JSON.parse(row.item['MESSAGE']),null,2)}}</pre>
        </b-popover> 
      </template>                     
   </b-table>
    <div class="detail-table-icons">
        <b-button v-on:click="firstPage" :disabled="isBusy" ref="firstPage" title="First Page"><span class="esri-icon-beginning"></span>
        </b-button>
        <b-button v-on:click="prevPage" :disabled="isBusy" ref="prevPage" title="Previous Page"><span class="esri-icon-reverse"></span>
        </b-button>
        <b-button v-on:click="nextPage" :disabled="isBusy" ref="nextPage" title="Next Page"><span class="esri-icon-forward" ></span>
        </b-button>
        <b-button v-on:click="lastPage" :disabled="isBusy" ref="lastPage" title="Last Page"><span class="esri-icon-end"></span>
        </b-button> 
        <span>Page:{{currentPage}}/{{getLastPage}}</span>
        <span><input style='width:4em;' placeholder='Page' :disabled="isBusy" v-model='goToPage'><b-button title="Go to Page" :disabled="isBusy" v-on:click='jumpToPage'>Go</b-button></span>
        <br/>        
        <span>Total Record:{{totalRows}}</span>
        <!-- <span v-if='orderByFields.length>0'>Sort By: {{orderByFields.join(',')}} <b-button title="Cancel Ordering" v-on:click='resetOrderBy'>Cancel</b-button> </span> -->
        <!-- <span v-show='isBusy' class="spinner">⌛</span> -->
        <!-- Test -->
    </div>

  </div>
</template>

<script>
import moment from 'moment';
import { exportCSVFile, exportCSVFile2, extractHeaders, extractFirstItemKeyAsHeaders } from "@/tools/exportCSVFile";
import { loadModules } from "esri-loader";
import { queryLayerInfo,applyEditRequest } from "@/service/restAPIHelper";

export default {
    props:{
        'onColumnClicked': {
            type: Function,
            default: undefined
        },
        'exportWithTimeInDateField':{
            type: Boolean,
            default:false
        },
        'removeUrl':{type:String,default:null},
        'enableAddRecord':{
            type: Boolean,
            default:false
        },
        'tableProvider':{
            type: null
        },
        'tableFields':{
            type:null
        },
        'layerUrl':{
            type:null
        },
        'perPage':{
            type: Number,
            default:5
        },
        'tableCaption':{
            type: null,
            default: null
        },
        'tableId':{
            type: String,
            default:'default-details-table'
        },
        'alwaysEmitAllRecords':{ //if enable, always emit full set of data when tableProvider changes. intend for heaving. 
            type: Boolean,
            default:false
        },
        'enableFilterByMap':{
            type: Boolean,
            default:false
        },
        'isFilterByMap':{
            type: Boolean,
            default:false
        },
        'disableEndFilter':{
            type: Boolean,
            default:false
        },
        'tableOpacity':{
            type:Number,
            default:1
        },
        'enableEmitAllRecordsButton':{
            type:Boolean,
            default:false
        },
        'queryProviderType':{
            type:String,
            default:'generateQueryProvider'
        },
        'exportRequire':{
            type:Number,
            default:1000
        },
        // 'subsystemCode':{
        //     type:String,
        //     default:null
        // },
        // 'removeMinLevel':{
        //     type:Number,
        //     default:null
        // },
        // 'systemAdminCode':{
        //     type:String,
        //     default:null
        // },
    },
    data:function(){
        return{
            isBusy: false,
            currentPage: 1,
            items:[],
            totalRows:0,
            orderByFields : [],
            orderByFieldsLabel :[],
            applyEditRequest:applyEditRequest,
            goToPage:null,
            orderByFieldsForAPI :[],
        }
    },
    methods:{
        toggleFilterMap: function () {
            this.$emit('filterByMap', !this.isFilterByMap)
        },
        // onFilterByMap(){
        //     this.isFilterByMap = !(this.isFilterByMap);
        //     this.$emit('filterByMap', this.isFilterByMap)
        // },
        monthFormatter(monthNum){
            var monthNum = moment(monthNum,"MM").format('MMM').toUpperCase();
            if(monthNum == 'Invalid month'){
                monthNum = ""
            }
            return monthNum
        },
        dateFormatter(date) {
            var dateString = moment(date).format('YYYY-MM-DD');
            if(dateString == 'Invalid date'){
                dateString = ""
            }
            return dateString
        },
        dateTimeFormatter(date){
            var dateString = moment(date).format('YYYY-MM-DD, h:mm:ss a');
            if(dateString == 'Invalid date'){
                dateString = ""
            }
            return dateString            
        },
        dateTimeHourMinutesFormatter(date){
            var dateString = moment(date).format('YYYY-MM-DD HH:mm');
            if(dateString == 'Invalid date'){
                dateString = ""
            }
            return dateString            
        },
        yearFormatter(date) {
            var dateString = moment(date).format('YYYY');
            if(dateString == 'Invalid date'){
                dateString = ""
            }
            return dateString
        },
              
        jumpToPage:function(){
            var pageToJumpTo = parseInt(this.goToPage);
            
            if(pageToJumpTo >= 1 && pageToJumpTo <= this.getLastPage){
                this.currentPage= pageToJumpTo
            }
            this.goToPage=null;    
        },
        firstPage:function(){
            this.currentPage = 1;
        },
        nextPage:function(){
            if(this.currentPage == this.getLastPage ){
                // window.alert('already at last page')
            }else{
                this.currentPage= this.currentPage+1   
            }
        },
        prevPage:function(){
            if(this.currentPage>1)
                this.currentPage = this.currentPage-1  
        },
        lastPage:function(){
            this.currentPage = this.getLastPage;
        },
        resetOrderBy:function(){
            this.orderByFields = []
            this.orderByFieldsLabel = []
            this.orderByFieldsForAPI = []
            if (this.onColumnClicked) {
                this.onColumnClicked(this.orderByFieldsForAPI);
                this.$emit('changeHeader');
            }
            else{
                this.$refs.table.refresh();
                }
        },
        alterOrderByFields:function(fieldName,fieldLabel){
            // console.log(fieldName);
            var fieldExistIndex = this.orderByFields.findIndex(function(field){
                if(field.indexOf(fieldName)>-1){
                    return true;
                }else{
                    return false;
                }
            })
            if(fieldExistIndex > -1){
                if(this.orderByFields[fieldExistIndex].indexOf(' DESC')>-1){
                    this.orderByFields.splice(fieldExistIndex,1, fieldName + " ASC")
                    this.orderByFieldsLabel.splice(fieldExistIndex,1, fieldLabel.label + " ASC")
                    this.orderByFieldsForAPI.splice(fieldExistIndex,1, fieldLabel.key + " ASC") // since if chcek the field is SHAPE.STArea() will change it to number for RestAPI
                }else{
                    this.orderByFields.splice(fieldExistIndex,1, fieldName + " DESC")
                    this.orderByFieldsLabel.splice(fieldExistIndex,1, fieldLabel.label + " DESC")
                    this.orderByFieldsForAPI.splice(fieldExistIndex,1, fieldLabel.key + " DESC")
                }
            }else{
                this.orderByFields.push(fieldName);
                this.orderByFieldsLabel.push(fieldLabel.label);
                this.orderByFieldsForAPI.push(fieldLabel.key);
            }
            if (this.onColumnClicked) {
                this.onColumnClicked(this.orderByFieldsForAPI);
                this.$emit('changeHeader');
                // this.$refs.table.refresh();

            }
            else{
                //tell table to refresh after alter orderByFields
                this.$refs.table.refresh();
            }
        },
        alterOrderByFieldsV2:function(fieldName,fieldLabel){
            // var fieldExistIndex = this.orderByFields.findIndex(function(field){
            //     if(field.indexOf(fieldName)>-1){
            //         return true;
            //     }else{
            //         return false;
            //     }
            // })
            // var orderByFields = ["SUB_COMPONENT", "COMPONENT"]
            // var fieldExistIndex=false;
            // for(var i in orderByFields){
            //     if(orderByFields[i]==fieldName){
            //         fieldExistIndex = true;
            //         break; 
            //     }
            // }
            // if(fieldExistIndex > -1){
            //     if(this.orderByFields[fieldExistIndex].indexOf('DESC')>-1){
            //         this.orderByFields.splice(fieldExistIndex,1, fieldName + " ASC")
            //         this.orderByFieldsLabel.splice(fieldExistIndex,1, fieldLabel + " ASC")
            //     }else{
            //         this.orderByFields.splice(fieldExistIndex,1, fieldName + " DESC")
            //         this.orderByFieldsLabel.splice(fieldExistIndex,1, fieldLabel + " DESC")
            //     }
            // }else{
            //     this.orderByFields.push(fieldName);
            //     this.orderByFieldsLabel.push(fieldLabel);
            // }
            
            // if (this.onColumnClicked) {
            //     this.onColumnClicked(this.orderByFields);
            //     this.$emit('changeHeader');
            //     // this.$refs.table.refresh();

            // }
            // else{
            //     //tell table to refresh after alter orderByFields
            //     this.$refs.table.refresh();
            // }
        },        
        onHeadClicked:function(fieldName, fieldLabel){
            
            var isActionField = false;
            this.tableFields.forEach(tableField => {
                if(tableField.actionField && tableField.key == fieldName){
                    isActionField = true;
                }
            });
            if(isActionField){
                return null;
            }

            //return earlier because the below field is not for ordering
            // if(fieldName == "pan" || fieldName == 'edit' || fieldName == 'info' || fieldName == 'remove'){
            //     return
            // }

            //if shape fields, find its index on the field list for orderBy,
            //else use field name is fine
            if(
                fieldName.indexOf('SHAPE.STArea()')>-1 ||
                fieldName.indexOf('SHAPE.STLength()') > -1
              ){
              queryLayerInfo(this.layerUrl).then((layerInfo)=>{
                var shapeFieldIndex = layerInfo.fields.findIndex(function(eachField){
                    return (eachField.name == fieldName)
                })

                  //convert to string type for string comparison
                  shapeFieldIndex = (shapeFieldIndex + 1).toString();
                  
                  this.alterOrderByFields(shapeFieldIndex,fieldLabel)
              });
            }else{
                 this.alterOrderByFields(fieldName.toString(),fieldLabel)
            }


        },
        add:function(){
            this.$emit('add')
        },
        pan:function(item, index, button) {
            //this.modalInfo.title = `Row index: ${index}`
            //this.modalInfo.content = JSON.stringify(item, null, 2)
            // console.log('pan', item,index,button)
            this.$emit('pan', item)
        },
        //for detail table action
        log:function(item, index, button) {
            //this.modalInfo.title = `Row index: ${index}`
            //this.modalInfo.content = JSON.stringify(item, null, 2)
            // console.log('log', item,index,button)
            this.$emit('log', item)
        },
        edit:function(item, index, button) {
            //this.modalInfo.title = `Row index: ${index}`
            //this.modalInfo.content = JSON.stringify(item, null, 2)
            // console.log('edit', item,index,button)
            this.$emit('edit', item)
        },
        image:function(item,index,button){
            // console.log('image', item,index,button)
            this.$emit('image',item);
        },
        //lux report
        report:function(item, index, button){
            // console.log('report', item,index,button)
            this.$emit('report', item)
        },

        viewResults:function(item, index, button){
            // console.log('viewResults', item,index,button)
            this.$emit('viewResults', item)
        },
        editResults:function(item, index, button){
            // console.log('editResults', item,index,button)
            this.$emit('editResults', item)
        },
        removeRelated:function(item, index, button) {
            if(window.confirm("Confirm to Delete?")){
                this.$emit('removeRelated', item)
            }
        },

        remove:function(item, index, button) {
            //this.modalInfo.title = `Row index: ${index}`
            //this.modalInfo.content = JSON.stringify(item, null, 2)
            // if(!this.$root.$refs.app.hasAccessRight(this.subsystemCode, this.removeMinLevel, this.systemAdminCode)){
            //     alert("You do not have access right to Remove the Record.");
            //     return
            // }
            // alert("You have access right to Remove the Record.");
            if(window.confirm("Confirm to Delete?")){
                var addJSON = null
                var updateJSON = null
                var deleteJSON = JSON.stringify([item.OBJECTID]);
                this.applyEditRequest(this.removeUrl,addJSON,updateJSON,deleteJSON,
                    (response)=>{
                        // console.log("Record deleted.",response);
                        alert("Record deleted.");
                        this.$emit('remove', response)
                    },
                    (response)=>{
                        // console.log("Failed to delete record.",response);
                        alert("Failed to delete record.");
                        this.$emit('remove', response)
                    }
                );
            }
        },
        audit:function(item, index, button) {
            //this.modalInfo.title = `Row index: ${index}`
            //this.modalInfo.content = JSON.stringify(item, null, 2)
            // console.log('audit', item,index,button)
            this.$emit('audit', item)
        },

        queryAllRecords_bak:function(){
            var totalRows = this.totalRows;
            return loadModules(["dojo/promise/all"])
            .then( ([all]) => {

                var resultSegments = [];

                var currentRow = 0;
                while(currentRow<totalRows){
                    var queryOptions = { num:1000,start: currentRow}
                    if(this.orderByFields && this.orderByFields.length>0 ){
                        queryOptions['orderByFields'] = this.orderByFields
                    }
                    resultSegments.push(  this.tableProvider(null,queryOptions) )
                    currentRow +=1000;
                }
                
                all(resultSegments).then( (allResultSegments) =>{

                //   console.log('resultSegments',allResultSegments);
                  var flattenResult = allResultSegments.flatten();
                //   console.log('resultSegmentsFlatten',flattenResult);  
                
                  this.$emit('allRecords_bak', flattenResult)
                });

            });

        },
        queryAllRecords:function(){
            var totalRows = this.totalRows;
            return loadModules(["dojo/promise/all"])
            .then( ([all]) => {

                var resultSegments = [];
                if(this.queryProviderType == "generateQueryProvider"){
                    var currentRow = 0;
                    while(currentRow<totalRows){
                        var queryOptions = { num:1000,start: currentRow}
                        if(this.orderByFields && this.orderByFields.length>0 ){
                            queryOptions['orderByFields'] = this.orderByFields
                        }
                        resultSegments.push(  this.tableProvider(null,queryOptions) )
                        currentRow +=1000;
                    }
                }
                if(this.queryProviderType == "sqlQueryProvider"){
                    var currentPage = 0;
                    while(currentPage<totalRows/this.exportRequire){
                        var queryOptions = { perPage:this.exportRequire,currentPage: currentPage+1}
                        if(this.orderByFields && this.orderByFields.length>0 ){
                            queryOptions['orderByFields'] = this.orderByFields
                        }
                        resultSegments.push( this.tableProvider(null,queryOptions) )
                        currentPage +=1;
                    }                    
                }            
                all(resultSegments).then( (allResultSegments) =>{

                //   console.log('resultSegments',allResultSegments);
                  var flattenResult = allResultSegments.flatten();
                //   console.log('resultSegmentsFlatten',flattenResult);  
                
                  this.$emit('allRecords', flattenResult)
                });

            });

        },
        endFilter:function(){
            this.$emit("endFilter");
        },
        download:function(){
            var totalRows = this.totalRows;
            return loadModules(["dojo/promise/all"])
            .then( ([all]) => {

                var resultSegments = [];

                if(this.queryProviderType == "generateQueryProvider"){
                    var currentRow = 0;
                    while(currentRow<totalRows){
                        var queryOptions = { num:1000,start: currentRow}
                        queryOptions['queryFor'] = 'download';
                        if(this.orderByFields && this.orderByFields.length>0 ){
                            queryOptions['orderByFields'] = this.orderByFields
                        }
                        resultSegments.push( this.tableProvider(null,queryOptions) )
                        currentRow +=1000;
                    }
                }
                if(this.queryProviderType == "sqlQueryProvider"){
                    var currentPage = 0;
                    while(currentPage<totalRows/this.exportRequire){
                        var queryOptions = { perPage:this.exportRequire,currentPage: currentPage+1}
                        if(this.orderByFields && this.orderByFields.length>0 ){
                            queryOptions['orderByFields'] = this.orderByFields
                        }
                        resultSegments.push( this.tableProvider(null,queryOptions) )
                        currentPage +=1;
                    }                    
                }

                
                all(resultSegments).then( (allResultSegments) =>{

                //   console.log('resultSegments',allResultSegments);
                  var flattenResult = allResultSegments.flatten();
                //   console.log('resultSegmentsFlatten',flattenResult);
                  
                  if(flattenResult && flattenResult.length == 0){
                      window.alert('There are no records for export')
                  }
                  if(this.exportWithTimeInDateField){
                      exportCSVFile2(
                        extractFirstItemKeyAsHeaders(flattenResult),
                        flattenResult,
                        'DetailsTable',
                    );
                  }else{
                      exportCSVFile(
                        extractFirstItemKeyAsHeaders(flattenResult),
                        flattenResult,
                        'DetailsTable',
                    );
                  }
                });

                // .then( (returnItems) =>{
                // exportCSVFile(
                //     extractFirstItemKeyAsHeaders(returnItems),
                //     returnItems,
                //     'DetailsTable',
                // )
            });



        }
    },
    computed: {
        opacity:function(){
            return "opacity: " + this.tableOpacity;
        },
        getLastPage: function () {
            // return this.perPage==0?1:(Math.floor( (this.totalRows==0?0:this.totalRows-1) / this.perPage ) + 1);
            if( (this.totalRows%this.perPage) == 0){
                return Math.floor( this.totalRows/ this.perPage ) 
            }else{
                return Math.floor( this.totalRows/ this.perPage ) + 1
            }

        }
    },  
    mounted:function(){
        this.isBusy = true;
    },
    watch:{

        //reset currentPage to 1 if there is a new table provider
        tableProvider:function(newTableProvider){
            this.currentPage = 1;

        },
        totalRows:function(newTotalRows){
            if(this.alwaysEmitAllRecords){
                this.queryAllRecords();
            }
        },
    }
}
</script>

<style>
.detail-table-icons .btn{
    padding: 0.175rem 0.55rem;
}
</style>



// WEBPACK FOOTER //
// src/components/ui-components/DetailsTable.vue